import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { ArrowUpRight, Dices, Tv, Video } from "lucide-react";

import { Card, SectionHeader, StatusPill } from "./ui";

type AppStatus = "success" | "info" | "idle";

const apps: {
  id: number;
  name: string;
  desc: string;
  cat: string;
  icon: LucideIcon;
  status: AppStatus;
  statusLabel: string;
  stat?: string;
  cta?: string;
}[] = [
  {
    id: 1,
    name: "BROADCAST TICKER",
    desc: "Advertising, news, stats - all in a compact ticker.",
    cat: "OVERLAY",
    icon: Tv,
    status: "success",
    statusLabel: "STABLE",
    stat: "PRESETS AVAILABLE",
    cta: "INSTALL",
  },
  {
    id: 2,
    name: "RACE WAGERING",
    desc: "Viewers can risk it all with fictional currencies and maximum interactions.",
    cat: "TOOL",
    icon: Dices,
    status: "info",
    statusLabel: "BETA",
    stat: "MULTIPLE GAMES",
  },
  {
    id: 3,
    name: "DIRECTOR'S SUITE",
    desc: "Professional-level broadcast camera switching and focus.",
    cat: "BROADCAST",
    icon: Video,
    status: "idle",
    statusLabel: "COMING SOON",
  },
];

export function AppCatalog() {
  return (
    <section id="apps" className="mx-auto max-w-7xl px-6 py-24">
      <SectionHeader
        eyebrow="The Catalog"
        title="Apps Built For Broadcasters"
        description="Mix and match from a growing library of overlays, interactive tools, and broadcast apps. Browser-source ready. Easy setup."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {apps.map((app, index) => (
          <motion.div
            key={app.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.06 }}
          >
            <Card className="flex h-full flex-col">
              <div className="mb-4 flex items-start justify-between">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-bg text-text transition-colors group-hover:border-primary/50 group-hover:text-primary">
                  <app.icon size={22} strokeWidth={1.5} />
                </div>
                <StatusPill status={app.status} label={app.statusLabel} />
              </div>

              <div className="mb-1 flex items-center gap-2">
                <h3 className="font-display text-lg font-bold uppercase tracking-wide text-text">{app.name}</h3>
              </div>
              <span className="mb-3 text-[10px] font-semibold tracking-[0.25em] text-primary">{app.cat}</span>

              <p className="mb-6 flex-1 text-sm text-muted">{app.desc}</p>

              <div className="mt-auto flex min-h-7 items-center justify-between border-t border-border pt-4">
                <span className="text-[10px] font-semibold tracking-[0.25em] text-muted">{app.stat ?? ""}</span>
                {app.cta ? (
                  <a
                    href="/download"
                    className="flex items-center gap-1 text-[11px] font-semibold tracking-[0.2em] text-muted transition-colors group-hover:text-primary"
                  >
                    {app.cta} <ArrowUpRight size={12} />
                  </a>
                ) : null}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
